import { cn } from '@/utils';
import { SkeletonLoader } from '@/components/Loaders';
import { TNormalizedRatingCutoffs, TScoreSkillLevel } from '@/types';
import { APP_SCORING_PERCENTAGE_RANGES } from '@/constants/config';

type TLabel = {
  key: string;
  text: string;
  ariaLabel: string;
  testId: string;
  dataLevel?: string;
};

/**
 * Subcomponent for rendering skill level labels in the SkillLevelScale component.
 * Receives the active index, loading state, and skill levels to display the appropriate labels.
 */
export const SkillLevelScaleLabels: React.FC<{
  activeIndex?: number;
  isLoading?: boolean;
  /**
   * Runtime-normalized rating cutoffs from backend API.
   * When provided, overrides static APP_SCORING_PERCENTAGE_RANGES
   * for percentage label rendering.
   */
  runtimeRatingCutoffs?: TNormalizedRatingCutoffs;
  skillLevels: TScoreSkillLevel[];
  type: 'skill-levels' | 'percentages';
}> = ({ activeIndex, isLoading, runtimeRatingCutoffs, skillLevels = [], type }) => {
  // ==================================
  // Logic
  // ==================================

  /**
   * Extracts the percentage ranges to use for label rendering.
   * Uses runtime cutoffs when provided, falling back to static app config.
   */
  const percentageRanges = Object.values(
    runtimeRatingCutoffs ?? APP_SCORING_PERCENTAGE_RANGES,
  );

  /**
   * Determines if the labels to display are percentage ranges or skill levels
   * based on the provided type.
   */
  const isPercentageType = type === 'percentages';

  /**
   * Constructs the label data based on the type of labels to display
   * (skill levels or percentage ranges).
   * - For skill levels, it uses the provided skillLevels array.
   * - For percentage ranges, it maps over the predefined percentage ranges to create labels.
   *
   * Each label includes accessibility attributes and test IDs for testing purposes.
   */
  const labels: TLabel[] = isPercentageType
    ? percentageRanges.map((range, index) => ({
        key: String(index),
        text: `${range[0]} - ${range[1]}%`,
        ariaLabel: `${range[0]} to ${range[1]} percent`,
        testId: `skill-level-percentage-label-${index}`,
      }))
    : skillLevels.map((skillLevel) => ({
        key: skillLevel,
        text: skillLevel,
        ariaLabel: skillLevel,
        testId: `skill-level-label-${skillLevel.toLowerCase()}`,
        dataLevel: skillLevel.toLowerCase(),
      }));

  // ==================================
  // Render
  // ==================================
  return (
    <div
      className={cn(
        'skilllevelscale-labels-container grid w-full items-start',
        isPercentageType ? 'mt-1' : 'mb-1',
      )}
      style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}
    >
      {labels.map((label, index) => (
        <div
          key={label.key}
          className="flex w-full min-w-0 flex-col items-center text-center"
          data-level={label.dataLevel}
          data-testid={label.testId}
        >
          {isLoading ? (
            <SkeletonLoader className="h-4 w-12" rounded="sm" />
          ) : (
            <span
              aria-label={label.ariaLabel}
              className={cn(
                'font-medium transition-colors duration-200',
                !isPercentageType && 'capitalize',
                index === activeIndex ? 'text-neutral-black' : 'text-neutral-gray-600',
              )}
            >
              {label.text}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};
